import { OnboardingIntent, parseOnboardingIntent } from 'fym-shared/src/music24/onboardingIntent';

const KEY = 'music24SignupIntent';
let memoryIntent: OnboardingIntent | null = null;
// The intent that this page load sent to the server (`useSyncSignupIntent`). A sign-out reloads
// the app, so it never reaches another user.
let sentIntent: OnboardingIntent | null = null;

/**
 * Signup intents of the website and the public tools that the onboarding does not know. The server
 * takes only `ONBOARDING_INTENTS` (`onboarding.setIntent`), so each one maps to the nearest intent
 * there: `music-video` (the Studio showcase, music24.com/music-video-maker) opens the Promote
 * onboarding, where the music video of a song lives.
 */
const SIGNUP_INTENT_ALIASES: ReadonlyMap<string, OnboardingIntent> = new Map<string, OnboardingIntent>([
  ['music-video', 'promote'],
  // music24.com/pitch and the playlist finder (plan 2026-10-11 Task 5): the Promote onboarding, and
  // `signupLanding.ts` opens Outreach → Pitch after the sign-up.
  ['outreach', 'promote'],
]);

/** The onboarding intent of an `?intent=` value: a known intent, an alias, or null. Pure. */
export function signupIntentOf(value: string | null): OnboardingIntent | null {
  return (value === null ? undefined : SIGNUP_INTENT_ALIASES.get(value)) ?? parseOnboardingIntent(value);
}

/** Keeps `?intent=` of the website "Start free" link (spec §8) until the first sign-in. */
export function captureSignupIntent(search: string = window.location.search): void {
  const intent = signupIntentOf(new URLSearchParams(search).get('intent'));
  if (intent === null) {
    return;
  }
  memoryIntent = intent;
  try {
    sessionStorage.setItem(KEY, intent);
  } catch {
    // Blocked storage: the memory copy lasts for this page view.
  }
}

export function readSignupIntent(): OnboardingIntent | null {
  try {
    return parseOnboardingIntent(sessionStorage.getItem(KEY)) ?? memoryIntent;
  } catch {
    return memoryIntent;
  }
}

export function clearSignupIntent(): void {
  memoryIntent = null;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
}

/** `useSyncSignupIntent` took the stored intent and sends it: the pages use it until the server answers. */
export function markSignupIntentSent(intent: OnboardingIntent): void {
  sentIntent = intent;
}

/**
 * The intent of the person for the welcome headline and the tour order. The server answer wins.
 * While the answer has no intent yet (the `setIntent` call is on its way, or the state loaded
 * before it), the intent of this page load fills the gap, so the headline does not change after
 * the first paint and the tour starts at the right stop.
 */
export function onboardingIntentOf(state: { intent: OnboardingIntent | null } | undefined): OnboardingIntent | null {
  return state?.intent ?? sentIntent ?? readSignupIntent();
}

/** Tests only. */
export function forgetSentSignupIntent(): void {
  sentIntent = null;
}
